import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";

import { Button } from "@acme/ui/button";

import { useCodemodAiPanel } from "../(studio)/studio/_hooks/use-codemod-ai-panel";
import { CodemodAiTrigger } from "./codemod-ai-trigger";

interface AskCodemodBannerProps {
  showCodemodBanner: boolean;
  setShowCodemodBanner: Dispatch<SetStateAction<boolean>>;
}

export function AskCodemodBanner({
  showCodemodBanner,
  setShowCodemodBanner,
}: AskCodemodBannerProps) {
  const [codemodAiThread] = useCodemodAiPanel();

  const hasViewedAi = useRef(!!codemodAiThread);

  if (codemodAiThread) {
    hasViewedAi.current = true;
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCodemodBanner(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [setShowCodemodBanner]);

  const actualVisible =
    !codemodAiThread && showCodemodBanner && !hasViewedAi.current;

  return (
    <AnimatePresence>
      {actualVisible && (
        <motion.div
          layout
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 44 }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5 }}
          className="flex shrink-0 overflow-hidden"
        >
          <div className="to-primary/10 flex w-full items-center justify-between border-b bg-linear-to-l from-transparent pr-2 pl-4 shadow-md">
            <div className="inline-flex items-center gap-2 text-sm font-medium">
              <span className="line-clamp-1">Need help with a Codemod?</span>
              <CodemodAiTrigger className="bg-background/50 hover:bg-background/75" />
            </div>

            <Button
              variant="ghost"
              className="h-auto rounded-full p-1"
              onClick={() => setShowCodemodBanner(false)}
            >
              <XIcon className="text-muted-foreground size-5" />
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
